import classNames from 'classnames'
import { t } from 'i18next'
import { Button } from 'primereact/button'
import { InputText } from 'primereact/inputtext'
import { Toast } from 'primereact/toast'
import React, { useEffect, useState } from 'react'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { API_GROUP_MEMBERS_BY_GROUP_ID } from '../../../../constants'
import { RequestMethod } from '../../../../model/enums/enums'
import { Group, GroupMember } from '../../../../model/group.model'
import { addGroupMember } from '../../../../redux/basic/group/groupActions'
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks'
import { handleRequest } from '../../../../services/api/base/handleRequest'
import {
  getFormErrorMessage,
  getRequiredFormMessage,
} from '../../../util/ErrorText'
import { AllPeopleDropdown } from '../../../util/form/dropdown/person-dropdown/AllPeopleDropdown'

const defaultGroupMember: GroupMember = {
  group_member_id: 0,
  id_person: -1,
  role_name: '',
  id_group: 0,
}

// separate form to allow group members to be added
export const AddGroupMemberForm = (props: {
  group_id: number;
  toastRef: React.RefObject<Toast>; // toast ref to display success/error messages
}): JSX.Element => {
  const dispatcher = useAppDispatch()

  const [currentMembersID, setCurrentMembersID] = useState<number[]>([])

  const currentMembers = useAppSelector((state) => {
    return state.groups.find(
      (group: Group) => group.group_id === props.group_id
    )
  })?.group_member

  // needed to exclude current members from list
  useEffect(() => {
    const membersID = currentMembers?.map(
      (member: GroupMember) => member.id_person
    )
    setCurrentMembersID(membersID || [])
  }, [currentMembers])

  // this is a separate form from change group name/description
  // so that they can be handled and used independently
  const methods = useForm({
    reValidateMode: 'onChange',
    mode: 'onSubmit',
    shouldFocusError: true,
    defaultValues: defaultGroupMember,
  })

  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false)

  // subscribe to set the buttons to disabled
  // if nothing has changed
  useEffect(() => {
    const subscription = methods.watch(async (value, { name, type }) => {
      if (type === 'change' || type === undefined) {
        setHasUnsavedChanges(true)
      }
    })
    return () => subscription.unsubscribe()
  }, [])

  const onSubmit = async (formData: GroupMember) => {
    const formDataCopy = { ...formData }
    formDataCopy.id_group = props.group_id
    const result = await handleRequest<GroupMember>(
      API_GROUP_MEMBERS_BY_GROUP_ID(props.group_id),
      RequestMethod.POST,
      formDataCopy
    )
    if (result.error || !result.data) {
      //TODO user feedback
      console.warn(
        `[Add Group Member] 
                    Something went wrong adding the group member to group`
      )
      return
    }

    dispatcher(addGroupMember(result.data.id_group, result.data))

    const defaultGroupMemberCopy = { ...defaultGroupMember }
    defaultGroupMember.id_group = props.group_id
    methods.reset(defaultGroupMemberCopy)
    setHasUnsavedChanges(false)

    props.toastRef.current?.show({
      closable: true,
      severity: 'success',
      summary: t<string>('core.action.save.success.title'),
      detail: t<string>('core.action.save.success.message'),
      life: 4000,
    })
  }

  const peopleDropwDownError = (): JSX.Element => {
    if (methods.formState.errors.id_person) {
      return (
        <small className="p-error">
          {getRequiredFormMessage(t<string>('core.person'))}
        </small>
      )
    }

    return <></>
  }

  return (
    <div>
      <b>{t<string>('hrm.group.member.add')}</b>
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          <div className="formgrid grid flex-wrap p-fluid pl-0 gap-1 mt-1">
            <div className="field col">
              <AllPeopleDropdown
                fieldName={'id_person'}
                excludedPeople={currentMembersID}
                required={true}
              />
              {peopleDropwDownError()}
            </div>
            <div className="field col">
              <label htmlFor={'role_name'}>
                {t<string>('hrm.group.member.role_name')}
              </label>
              <Controller
                name={'role_name'}
                control={methods.control}
                rules={{
                  maxLength: 50,
                }}
                render={({ field, fieldState }) => (
                  <InputText
                    id={field.name}
                    {...field}
                    value={field.value || ''}
                    maxLength={50}
                    placeholder={t<string>('hrm.group.member.placeholder')}
                    className={classNames(
                      { 'p-invalid': fieldState.error },
                      ' people-dropdown'
                    )}
                  />
                )}
              />
              {getFormErrorMessage(methods.formState.errors, 'role_name')}
            </div>

            <div className={'col-12 md:col-2 flex align-items-center pl-2'}>
              <Button
                type={'submit'}
                label={t<string>('hrm.group.member.add')}
                className={'p-button'}
                disabled={!hasUnsavedChanges}
              />
            </div>
          </div>
        </form>
      </FormProvider>
    </div>
  )
}